import React, { useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { channelApi, tiktokApi } from '../utils/api';
import { YouTubeSettings, TikTokSettings } from '../types';

const s = {
  label: { fontSize: 12, color: '#888', marginBottom: 6, display: 'block' } as React.CSSProperties,
  input: {
    width: '100%', boxSizing: 'border-box' as const,
    padding: '7px 10px', fontSize: 13, border: '1px solid #e5e5e5',
    borderRadius: 5, color: '#111', outline: 'none',
  } as React.CSSProperties,
  row: { display: 'flex', gap: 8, alignItems: 'center' } as React.CSSProperties,
  btn: (primary?: boolean, disabled?: boolean) => ({
    padding: '7px 14px', fontSize: 13, borderRadius: 5,
    border: primary ? 'none' : '1px solid #e5e5e5',
    background: primary ? '#6441a5' : '#fff',
    color: primary ? '#fff' : '#555',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    whiteSpace: 'nowrap',
  } as React.CSSProperties),
  section: { marginBottom: 24 } as React.CSSProperties,
  note: { fontSize: 12, color: '#aaa', marginTop: 6 } as React.CSSProperties,
  divider: { borderTop: '1px solid #f0f0f0', marginTop: 28, marginBottom: 28 } as React.CSSProperties,
};

function Toggle({ enabled, canEnable, isPending, onToggle }: {
  enabled: boolean; canEnable: boolean; isPending: boolean;
  onToggle: () => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <button
        onClick={onToggle}
        disabled={isPending || (!enabled && !canEnable)}
        style={{
          width: 40, height: 22, borderRadius: 11, border: 'none',
          cursor: (!enabled && !canEnable) ? 'not-allowed' : 'pointer',
          background: enabled ? '#6441a5' : '#ddd',
          position: 'relative', transition: 'background 0.2s', padding: 0,
          opacity: isPending ? 0.6 : 1,
        }}
      >
        <span style={{
          position: 'absolute', top: 3, left: enabled ? 21 : 3,
          width: 16, height: 16, borderRadius: '50%', background: '#fff',
          transition: 'left 0.2s',
        }} />
      </button>
      <span style={{ fontSize: 13, color: '#555' }}>
        {enabled ? 'Integration enabled' : 'Integration disabled'}
      </span>
      {!canEnable && !enabled && (
        <span style={{ fontSize: 12, color: '#e57373' }}>Set reward name first</span>
      )}
    </div>
  );
}

const RewardSettings: React.FC<{ channelId: number }> = ({ channelId }) => {
  const [ytRewardName, setYtRewardName] = useState('');
  const [ttRewardName, setTtRewardName] = useState('');
  const queryClient = useQueryClient();

  // YouTube
  const { data: youtubeSettings } = useQuery({
    queryKey: ['youtube-settings', channelId],
    queryFn: () => channelApi.getYouTubeSettings(channelId).then(res => res.data),
  });

  const saveYtNameMutation = useMutation({
    mutationFn: (s: Partial<YouTubeSettings>) => channelApi.updateYouTubeSettings(channelId, s),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['youtube-settings', channelId] }),
  });

  const toggleYtMutation = useMutation({
    mutationFn: (s: Partial<YouTubeSettings>) => channelApi.updateYouTubeSettings(channelId, s),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['youtube-settings', channelId] }),
  });

  React.useEffect(() => {
    if (youtubeSettings?.reward_name) setYtRewardName(youtubeSettings.reward_name);
  }, [youtubeSettings]);

  const ytCanEnable = ytRewardName.trim() !== '';
  const ytEnabled = youtubeSettings?.enabled ?? false;

  // TikTok
  const { data: tiktokSettings } = useQuery({
    queryKey: ['tiktok-settings', channelId],
    queryFn: () => tiktokApi.getSettings(channelId).then(res => res.data),
  });

  const saveTtNameMutation = useMutation({
    mutationFn: (s: Partial<TikTokSettings>) => tiktokApi.updateSettings(channelId, s),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tiktok-settings', channelId] }),
  });

  const toggleTtMutation = useMutation({
    mutationFn: (s: Partial<TikTokSettings>) => tiktokApi.updateSettings(channelId, s),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tiktok-settings', channelId] }),
  });

  React.useEffect(() => {
    if (tiktokSettings?.reward_name) setTtRewardName(tiktokSettings.reward_name);
  }, [tiktokSettings]);

  const ttCanEnable = ttRewardName.trim() !== '';
  const ttEnabled = tiktokSettings?.enabled ?? false;

  return (
    <div>
      {/* YouTube */}
      <h2 style={{ fontSize: 16, fontWeight: 600, color: '#111', marginBottom: 20 }}>YouTube</h2>

      <div style={s.section}>
        <label style={s.label}>Reward name</label>
        <div style={s.row}>
          <input
            style={s.input}
            value={ytRewardName}
            onChange={e => setYtRewardName(e.target.value)}
            onKeyPress={e => e.key === 'Enter' && ytRewardName.trim() && saveYtNameMutation.mutate({ reward_name: ytRewardName.trim() })}
            placeholder="e.g. Play YouTube Video"
          />
          <button
            style={s.btn(true, !ytRewardName.trim() || saveYtNameMutation.isPending)}
            disabled={!ytRewardName.trim() || saveYtNameMutation.isPending}
            onClick={() => saveYtNameMutation.mutate({ reward_name: ytRewardName.trim() })}
          >
            {saveYtNameMutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
        <div style={s.note}>Must match your Twitch Channel Points reward name exactly.</div>
      </div>

      <Toggle
        enabled={ytEnabled}
        canEnable={ytCanEnable}
        isPending={toggleYtMutation.isPending}
        onToggle={() => { if (!ytEnabled && !ytCanEnable) return; toggleYtMutation.mutate({ enabled: !ytEnabled }); }}
      />

      <div style={s.divider} />

      {/* TikTok */}
      <h2 style={{ fontSize: 16, fontWeight: 600, color: '#111', marginBottom: 20 }}>TikTok</h2>

      <div style={s.section}>
        <label style={s.label}>Reward name</label>
        <div style={s.row}>
          <input
            style={s.input}
            value={ttRewardName}
            onChange={e => setTtRewardName(e.target.value)}
            onKeyPress={e => e.key === 'Enter' && ttRewardName.trim() && saveTtNameMutation.mutate({ reward_name: ttRewardName.trim() })}
            placeholder="e.g. Play TikTok Video"
          />
          <button
            style={s.btn(true, !ttRewardName.trim() || saveTtNameMutation.isPending)}
            disabled={!ttRewardName.trim() || saveTtNameMutation.isPending}
            onClick={() => saveTtNameMutation.mutate({ reward_name: ttRewardName.trim() })}
          >
            {saveTtNameMutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
        <div style={s.note}>Must match your Twitch Channel Points reward name exactly.</div>
      </div>

      <Toggle
        enabled={ttEnabled}
        canEnable={ttCanEnable}
        isPending={toggleTtMutation.isPending}
        onToggle={() => { if (!ttEnabled && !ttCanEnable) return; toggleTtMutation.mutate({ enabled: !ttEnabled }); }}
      />
    </div>
  );
};

export default RewardSettings;
